/* Mailing and newsletter management page in the shared admin page frame.
   Restyled: warm-neutral Buitn Planten palette; stat cards, buttons, and empty states unified. */



import { useState } from 'react';

import Breadcrumb from '@/components/admin/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Users, Send, ChartBar as BarChart3, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Download, Upload } from 'lucide-react';

interface Subscriber {
  id: string;
  email: string;
  name: string;
  subscribed_at: string;
  active: boolean;
}

export default function MailingPage() {
  const [subscribers] = useState<Subscriber[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [newSubscriber, setNewSubscriber] = useState({ email: '', name: '' });

  const handleAddSubscriber = () => {
    if (!newSubscriber.email.trim()) {
      setMessage({ type: 'error', text: 'E-mailadres is verplicht' });
      return;
    }

    setMessage({ type: 'success', text: 'Abonnee succesvol toegevoegd' });
    setNewSubscriber({ email: '', name: '' });
  };

  const stats = [
    { label: 'Totaal abonnees', value: subscribers.length, icon: Users },
    { label: 'Actieve abonnees', value: subscribers.filter(s => s.active).length, icon: CheckCircle2 },
    { label: 'Verzonden emails', value: '0', icon: Send },
    { label: 'Open Rate', value: '0%', icon: BarChart3 }
  ];

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Breadcrumb pageName="Nieuwsbrief" />

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#1e2b22] mb-2">Nieuwsbrief</h1>
          <p className="text-[#5a6552]">Beheer e-mail campagnes en abonnees</p>
        </div>

        {message && (
          <Alert
            variant={message.type === 'error' ? 'destructive' : 'default'}
            className={`mb-6 ${message.type === 'success' ? 'border-[#1e2b22]/30 bg-[#e7e1d6]/40' : ''}`}
          >
            {message.type === 'success' ? (
              <CheckCircle2 className="h-4 w-4 text-[#1e2b22]" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-[#1e2b22]' : ''}>{message.text}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} className="border border-[#e7e1d6] bg-white hover:border-[#1e2b22]/40 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="bg-[#e7e1d6] text-[#1e2b22] p-3 rounded-lg">
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>
                  <p className="text-sm text-[#5a6552] mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold text-[#1e2b22]">{stat.value}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Tabs defaultValue="subscribers" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-[#e7e1d6]/60">
            <TabsTrigger value="subscribers" className="data-[state=active]:bg-white data-[state=active]:text-[#1e2b22]">Abonnees</TabsTrigger>
            <TabsTrigger value="campaigns" className="data-[state=active]:bg-white data-[state=active]:text-[#1e2b22]">Campagnes</TabsTrigger>
            <TabsTrigger value="templates" className="data-[state=active]:bg-white data-[state=active]:text-[#1e2b22]">Sjablonen</TabsTrigger>
          </TabsList>

          <TabsContent value="subscribers" className="space-y-6">
            <Card className="border border-[#e7e1d6] bg-white">
              <CardHeader className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
                <CardTitle className="text-[#1e2b22]">Nieuwe abonnee toevoegen</CardTitle>
                <CardDescription className="text-[#5a6552]">Voeg handmatig een abonnee toe aan de nieuwsbrief</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-[#1e2b22]">E-mailadres *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newSubscriber.email}
                      onChange={(e) => setNewSubscriber({ ...newSubscriber, email: e.target.value })}
                      placeholder="naam@voorbeeld.nl"
                      className="border-[#e7e1d6] focus-visible:ring-[#1e2b22]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="name" className="text-[#1e2b22]">Naam (optioneel)</Label>
                    <Input
                      id="name"
                      value={newSubscriber.name}
                      onChange={(e) => setNewSubscriber({ ...newSubscriber, name: e.target.value })}
                      placeholder="Volledige naam"
                      className="border-[#e7e1d6] focus-visible:ring-[#1e2b22]"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleAddSubscriber} className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white">
                    Toevoegen
                  </Button>
                  <Button variant="outline" className="border-[#e7e1d6] text-[#5a6552] hover:bg-[#e7e1d6]/40 hover:text-[#1e2b22]">
                    <Upload className="w-4 h-4 mr-2" />
                    Importeren (CSV)
                  </Button>
                  <Button variant="outline" className="border-[#e7e1d6] text-[#5a6552] hover:bg-[#e7e1d6]/40 hover:text-[#1e2b22]">
                    <Download className="w-4 h-4 mr-2" />
                    Exporteren
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-[#e7e1d6] bg-white">
              <CardHeader className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
                <CardTitle className="text-[#1e2b22]">Abonneelijst ({subscribers.length})</CardTitle>
                <CardDescription className="text-[#5a6552]">Overzicht van alle nieuwsbrief abonnees</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {subscribers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Users className="w-16 h-16 text-[#cfcac2] mb-4" />
                    <p className="text-[#1e2b22] text-lg mb-2">Nog geen abonnees</p>
                    <p className="text-[#5a6552]">Voeg handmatig abonnees toe of importeer een lijst</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {subscribers.map((subscriber) => (
                      <div key={subscriber.id} className="flex items-center justify-between p-3 border border-[#e7e1d6] rounded-lg">
                        <div>
                          <p className="font-semibold text-[#1e2b22]">{subscriber.name || subscriber.email}</p>
                          {subscriber.name && (
                            <p className="text-sm text-[#5a6552]">{subscriber.email}</p>
                          )}
                        </div>
                        <div className="text-sm text-[#5a6552]">
                          {new Date(subscriber.subscribed_at).toLocaleDateString('nl-NL')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="campaigns" className="space-y-6">
            <Card className="border border-[#e7e1d6] bg-white">
              <CardHeader className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
                <CardTitle className="flex items-center gap-2 text-[#1e2b22]">
                  <Send className="w-5 h-5" />
                  E-mail Campagnes
                </CardTitle>
                <CardDescription className="text-[#5a6552]">Creëer en verstuur e-mail campagnes naar je abonnees</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center py-12">
                  <Mail className="w-16 h-16 text-[#cfcac2] mb-4" />
                  <p className="text-[#1e2b22] text-lg mb-2">Nog geen campagnes</p>
                  <p className="text-[#5a6552] mb-4">Start met het maken van je eerste e-mail campagne</p>
                  <Button className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white">
                    <Send className="w-4 h-4 mr-2" />
                    Nieuwe campagne
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="templates" className="space-y-6">
            <Card className="border border-[#e7e1d6] bg-white">
              <CardHeader className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
                <CardTitle className="text-[#1e2b22]">E-mail Sjablonen</CardTitle>
                <CardDescription className="text-[#5a6552]">Beheer herbruikbare e-mail sjablonen</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center py-12">
                  <Mail className="w-16 h-16 text-[#cfcac2] mb-4" />
                  <p className="text-[#1e2b22] text-lg mb-2">Nog geen sjablonen</p>
                  <p className="text-[#5a6552] mb-4">Maak sjablonen om snel e-mails te kunnen versturen</p>
                  <Button className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white">
                    Nieuw sjabloon
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
